import React, { useMemo, useState, useEffect, useContext, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AuthContext } from "../../../context/AuthContext";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "../../../app/settingsSlice";
import { selectActiveFyid } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import GridDesign from "../../GridDesign/GridDesign";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";

const getDefaultColumns = () => {
  const list = [
    { key: "atType",           label: "Type",            width: 100 },
    { key: "Agent",            label: "Agent",           width: 110 },
    { key: "atdate",           label: "Date",            width: 90,  align: "center" },
    { key: "atEntryno",        label: "Entryno",         width: 80,  align: "center" },
    { key: "accno",            label: "AccNo",           width: 80,  align: "right"  },
    { key: "ledname",          label: "Customer",        width: 150 },
    { key: "atCreditAmount",   label: "Amount",          width: 110, align: "right" },
    { key: "atCreditWeight",   label: "Weight",          width: 90,  align: "right" },
    { key: "commPerc",         label: "Comm %",          width: 75,  align: "right" },
    { key: "commissionAmount", label: "Commission",      width: 110, align: "right" },
    { key: "atNarration",      label: "Narration",       width: 140 },
  ];
  return list.map((col, idx) => ({
    ...col,
    columnName: col.key,
    header: col.label,
    orderNo: idx,
    show: true,
  }));
};

const FORM_NAME = "Agent Commission Report";

function AgentReport({ onClose, agentReportData, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const activeFyid = useSelector(selectActiveFyid);
  const fyid = activeFyid || Number(sessionStorage.getItem("selectedFyId")) || Number(localStorage.getItem("selectedFyId")) || 0;
  const activeLocation = useSelector(selectActiveLocation);
  const GeneralSettings = useSelector(selectGeneralSettings);
  const [roundOff, setRoundOff] = useState(2);
  useEffect(() => {
    const obj = GeneralSettings.filter((item) => item.Name === "DECIMAL POINT IN REPORT");
    if (obj.length > 0 && obj[0].s_Value) setRoundOff(parseInt(obj[0].s_Value));
  }, [GeneralSettings]);

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns());
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ── Load grid settings ─────────────────────────────────────────────────────
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode) return;
      const defaults = getDefaultColumns();
      const cached = localStorage.getItem(`grid_design_${FORM_NAME}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.some((c) => c.key === "intPerc" || c.key === "IntrestAmount" || c.key === "dayss")) {
            localStorage.removeItem(`grid_design_${FORM_NAME}`);
            setGridColumns(defaults);
            return;
          }
          setGridColumns(parsed);
          return;
        } catch (e) {}
      }
      try {
        const res = await axios.get(`${apiBaseUrl}/main/griddesign/${dbCode}/${FORM_NAME}`);
        const dbCols = res.data.recordset || res.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((initCol) => {
            const m = dbCols.find((c) => String(c.ColName).toLowerCase() === String(initCol.columnName).toLowerCase());
            return m
              ? { ...initCol, width: m.Width ?? initCol.width, orderNo: m.InNo ?? initCol.orderNo, header: m.Header || initCol.header, show: m.Vis === 1 }
              : initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${FORM_NAME}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else {
          setGridColumns(defaults);
        }
      } catch {
        setGridColumns(defaults);
      }
    };
    loadGridSettings();
  }, [dbCode, apiBaseUrl]);

  const visibleColumns = useMemo(() => gridColumns.filter((col) => col.show), [gridColumns]);
  const gridTemplateColumns = useMemo(() => visibleColumns.map((col) => `${col.width}px`).join(" "), [visibleColumns]);

  // ── Fetch Data ─────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      const response = await axios.post(
        `${apiBaseUrl}/main/agentCommisssion_Report/${dbCode}`,
        { 
          ...agentReportData, 
          locationId: agentReportData.locationId ?? agentReportData.location ?? (activeLocation ? activeLocation.id : 0),
          location: agentReportData.location ?? agentReportData.locationId ?? (activeLocation ? activeLocation.id : 0),
          fyid: agentReportData?.fyid || fyid || 0 
        }
      );
      setTableData(response.data || []);
    } catch (error) {
      console.error("Error fetching agent data:", error);
      setErrorMsg("Error connecting to server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dbCode) fetchData();
  }, [apiBaseUrl, agentReportData, dbCode, activeLocation, fyid]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  // ── Formatting ─────────────────────────────────────────────────────
  const formatNumber = (value, decimals = roundOff) => {
    if (value === null || value === undefined || value === "" || value === "null") return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatValue = (row, key) => {
    switch (key) {
      case "atType":           return row.atType || "";
      case "Agent":            return row.Agent || "";
      case "atdate":           return row.atdate || "";
      case "atEntryno":        return row.atEntryno ? `${row.atEntryno}${row.Agent ? "-" + row.Agent.substring(0, 3) : ""}` : "";
      case "accno":            return row.accno || "";
      case "ledname":          return row.ledname || "";
      case "atCreditAmount":   return formatNumber(row.atCreditAmount, roundOff);
      case "atCreditWeight":   return row.atCreditWeight != null && row.atCreditWeight !== "" && Number(row.atCreditWeight) > 0 ? formatNumber(row.atCreditWeight, 3) : "";
      case "commPerc":         return row.commPerc != null && row.commPerc !== "" && Number(row.commPerc) > 0 ? `${formatNumber(row.commPerc, 2)}%` : "";
      case "commissionAmount": return row.commissionAmount != null && row.commissionAmount !== "" && Number(row.commissionAmount) > 0 ? formatNumber(row.commissionAmount, roundOff) : "";
      case "atNarration":      return row.atNarration || "";
      default:                 return "";
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      visibleColumns.some((col) => String(formatValue(row, col.key)).toLowerCase().includes(query))
    );
  }, [tableData, searchQuery, visibleColumns]);

  // ── Dynamic Totals ─────────────────────────────────────────────────────────
  const dynamicTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        const isSummaryRow = row.atType === "Sub Total" || row.atType === "Total" || (row.MonthName && row.atType === row.MonthName);
        if (!isSummaryRow) {
          acc.atCreditAmount += parseFloat(row.atCreditAmount) || 0;
          acc.atCreditWeight += parseFloat(row.atCreditWeight) || 0;
          acc.commissionAmount += parseFloat(row.commissionAmount) || 0;
        }
        return acc;
      },
      { atCreditAmount: 0, atCreditWeight: 0, commissionAmount: 0 }
    );
  }, [filteredRows]);

  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const { exportingText, runExport } = useReportExport();

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const exportToCsv = () => {
    runExport("Generating Excel / CSV...", () => {
      const header = visibleColumns.map((col) => col.header).join(",");
      const rows = filteredRows.map((row) =>
        visibleColumns.map((col) => `"${String(formatValue(row, col.key)).replace(/"/g, '""')}"`).join(",")
      );
      const csv = [header, ...rows].join("\n");
      const link = document.createElement("a");
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
      link.download = "agent_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const exportToFullPDF = () => {
    runExport("Generating PDF Report...", () => {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const columns = visibleColumns.map((col, idx) => ({ header: col.header, dataKey: `col${idx + 1}` }));
      const rows = filteredRows.map((item) => {
        const row = {};
        visibleColumns.forEach((col, idx) => { row[`col${idx + 1}`] = formatValue(item, col.key); });
        return row;
      });
      doc.text("Agent Commission Report", 15, 20);
      doc.autoTable({ columns, body: rows, startY: 30, styles: { fontSize: 8 } });
      doc.save("agent_report.pdf");
    });
  };

  // Stable column resize handler (shared ResizableHeader imported at module scope)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <div
      className="bg-white mt-[2vh] sm:mt-0 font-normal w-[98vw] h-[96vh] rounded-[5px] shadow-md flex flex-col items-center absolute transition-all duration-300 md:w-[98%] md:h-[98%]"
      style={{ zIndex }}
    >
      <style>{`
        .scheme-report-border { border: 1px solid #ffffff; }
        .report-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .report-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
        .report-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .report-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>

      {/* ── Window Header ── */}
      <WindowHeader
        onClose={onClose}
        title="Agent Commission Report"
        logo={headeLogo}
        style={{ height: "30px" }}
        className="bg-[#0f56d3] text-white"
        titleClassName="text-xs"
        closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
      />

      {/* ── Action Toolbar ── */}
      <ActionToolbar
        size="small"
        iconSize={16}
        buttonHeight="36px"
        showSave={false}
        showPrint
        showClear={false}
        showFind={false}
        showEdit={false}
        showDelete={false}
        showExit
        showPdf
        showExcel
        showSettingsBar
        showRefresh
        showNavigation={false}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onPrint={() => window.print()}
        onExcel={exportToCsv}
        onPdf={exportToFullPDF}
        onExit={onClose}
        settingsOptions={[{ label: "Design Columns", onClick: () => setShowGridDesignModal(true) }]}
      />

      {/* ── Body ── */}
      <div className="w-full flex-1 min-h-0 flex flex-col bg-white">

        {/* Info Panel + Search */}
        <div className="w-full border-b border-gray-200 bg-gradient-to-r from-slate-50 to-blue-50/20 px-4 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-Inter text-slate-700 flex-1">

            {/* Period Badge */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-slate-500 font-Inter">Period:</span>
              <span className="font-medium text-slate-900 font-Inter">
                {agentReportData?.fromDate || "-"} to {agentReportData?.toDate || "-"}
              </span>
            </div>

            {/* Agent Badge */}
            {agentReportData?.agent?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-semibold text-slate-500 font-Inter">Agent:</span>
                <span className="font-medium text-indigo-700 font-Inter">{agentReportData.agent.label}</span>
              </div>
            )}

            {/* Total Collection KPI */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <span className="font-semibold text-slate-500 font-Inter">Total Collection:</span>
              <span className="font-bold text-slate-900 font-Inter">₹ {formatNumber(dynamicTotals.atCreditAmount)}</span>
            </div>

            {/* Total Weight KPI */}
            {dynamicTotals.atCreditWeight > 0 && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <span className="font-semibold text-slate-500 font-Inter">Total Weight:</span>
                <span className="font-bold text-slate-900 font-Inter">{formatNumber(dynamicTotals.atCreditWeight, 3)} g</span>
              </div>
            )}

            {/* Total Commission KPI */}
            {dynamicTotals.commissionAmount > 0 && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <span className="font-semibold text-slate-500 font-Inter">Total Commission:</span>
                <span className="font-bold text-slate-900 font-Inter">₹ {formatNumber(dynamicTotals.commissionAmount)}</span>
              </div>
            )}
          </div>

          {/* Quick Search */}
          <div className="flex items-center shrink-0">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-7 py-1.5 text-xs border border-slate-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-Inter font-semibold text-slate-800 bg-white shadow-sm transition-all placeholder-slate-400"
              />
              <svg className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1">✕</button>
              )}
            </div>
          </div>
        </div>

        {/* Grid + Loader */}
        <div
          ref={tableContainerRef}
          className="w-full flex-1 min-h-0 overflow-auto text-black text-xs font-semibold report-scroll outline-none focus:ring-1 focus:ring-blue-400"
          tabIndex={0}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 border-[#0f56d3] border-t-transparent rounded-full animate-spin" />
                <span className="font-Inter text-[13px] text-gray-600 font-medium">Fetching Report Data...</span>
              </div>
            </div>
          ) : errorMsg ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-Inter text-[13px] text-red-500 font-medium">{errorMsg}</span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-Inter text-[13px] text-gray-400 font-medium">No records found for the selected filters.</span>
            </div>
          ) : (
            <div className="min-w-max flex flex-col min-h-full">
              {/* Sticky Header Row */}
              <div className="sticky top-0 z-10 bg-[#0f56d3] scheme-report-border shrink-0">
                <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                  {visibleColumns.map((col) => (
                    <ResizableHeader key={col.key} col={col} formName={FORM_NAME} onResize={handleColumnResize}>
                      <div className="text-white truncate text-center font-bold font-Inter scheme-report-border text-[11px] w-full h-full flex items-center justify-center px-1">
                         {col.header}
                      </div>
                    </ResizableHeader>
                  ))}
                </div>
              </div>

              {/* Data Scrollable Body */}
              <div className="bg-gray-100 flex-1">
                {displayedRows.map((row, rowIndex) => {
                  const isTotal = row.atType === "Total";
                  const isSubTotal = row.atType === "Sub Total";
                  const isMonthSubTotal = Boolean(row.MonthName && row.atType === row.MonthName);
                  const isSelected = rowIndex === selectedIndex;
                  return (
                    <div
                      key={rowIndex}
                      data-row-index={rowIndex}
                      onClick={() => setSelectedIndex(rowIndex)}
                      className={`grid gap-0 h-8 border-b border-gray-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${
                        isSelected
                          ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                          : isSubTotal
                            ? "bg-red-50 text-red-700 font-bold"
                            : isTotal
                              ? "bg-[#dbeafe] text-blue-800 font-bold"
                              : isMonthSubTotal
                                ? "bg-slate-200/80 text-slate-800 font-semibold italic"
                                : rowIndex % 2 === 0
                                  ? "bg-[#f7f7f7]"
                                  : "bg-[#eeeeee]"
                      }`}
                      style={{ gridTemplateColumns }}
                    >
                      {visibleColumns.map((col) => (
                        <div
                          key={col.key}
                          className={`font-Inter text-[11px] font-semibold scheme-report-border px-2 flex items-center truncate ${
                            col.align === "right" ? "justify-end text-right"
                            : col.align === "center" ? "justify-center text-center" : ""
                          }`}
                        >
                          {formatValue(row, col.key)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <TablePagination
          currentPage={currentPage}
          totalRows={totalRows}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          autoAdvance={autoAdvance}
          onToggleAutoAdvance={toggleAutoAdvance}
          isFlipping={isFlipping}
        />
      </div>

      {/* Grid Design Modal */}
      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => { setGridColumns(updated); setShowGridDesignModal(false); }}
          formName={FORM_NAME}
          initialColumns={getDefaultColumns()}
          zIndex={zIndex}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default AgentReport;